import { useState, useCallback, useRef, useEffect } from 'react'
import type { ManagedStudent, ManagedGroup, GradeOption, PaginationMeta } from 'api/arcMosaico/types'
import {
  listStudents,
  updateStudent,
  deleteStudent,
  resetStudentPassword,
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  listGrades,
} from '../service/manageStudentsService'
import { useArcDiskDemoEnabled, withDemoStudents } from 'arcdisk'

interface Filters {
  search: string
  groupId: string | null
  page: number
}

const ITEMS_PER_PAGE = 9

export function useManageStudents() {
  const [students, setStudents] = useState<ManagedStudent[]>([])
  const [groups, setGroups] = useState<ManagedGroup[]>([])
  const [grades, setGrades] = useState<GradeOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [filters, setFilters] = useState<Filters>({
    search: '',
    groupId: null,
    page: 1,
  })

  const mountedRef = useRef(true)
  const fetchAbortRef = useRef<AbortController | null>(null)
  const arcDiskDemo = useArcDiskDemoEnabled()

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      fetchAbortRef.current?.abort()
    }
  }, [])

  const fetchStudents = useCallback(async (currentFilters?: Filters) => {
    fetchAbortRef.current?.abort()
    const abortController = new AbortController()
    fetchAbortRef.current = abortController

    const f = currentFilters || filters
    setLoading(true)
    setError(null)

    try {
      const data = await listStudents({
        page: f.page,
        limit: ITEMS_PER_PAGE,
        search: f.search || undefined,
        groupSchoolTermId: f.groupId || undefined,
      })

      if (abortController.signal.aborted || !mountedRef.current) return

      // ArcDisk demo: the six demo students join page 1 of the unfiltered list
      setStudents(arcDiskDemo ? withDemoStudents(data.students, f) : data.students)
      setPagination(data.pagination)
    } catch (err) {
      if (abortController.signal.aborted || !mountedRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load students')
    } finally {
      if (!abortController.signal.aborted && mountedRef.current) {
        setLoading(false)
      }
    }
  }, [filters, arcDiskDemo])

  const fetchGroups = useCallback(async () => {
    try {
      const data = await listGroups()
      if (mountedRef.current) setGroups(data)
    } catch {
      // Non-critical
    }
  }, [])

  const fetchGrades = useCallback(async () => {
    try {
      const data = await listGrades()
      if (mountedRef.current) setGrades(data)
    } catch {
      // Non-critical
    }
  }, [])

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchStudents(), fetchGroups(), fetchGrades()])
  }, [fetchStudents, fetchGroups, fetchGrades])

  // Refetch students when filters change
  useEffect(() => {
    fetchStudents(filters)
  }, [filters.page, filters.groupId, filters.search])

  // Initial load for groups and grades
  useEffect(() => {
    fetchGroups()
    fetchGrades()
  }, [])

  // Filter setters (reset page on filter change)
  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }, [])

  const setGroupFilter = useCallback((groupId: string | null) => {
    setFilters(prev => ({ ...prev, groupId, page: 1 }))
  }, [])

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }, [])

  // Mutations

  const editStudent = useCallback(async (
    id: string,
    data: {
      firstName?: string
      lastName?: string
      username?: string
      email?: string
      gradeId?: string | null
      groupSchoolTermIds?: string[]
    },
  ) => {
    await updateStudent(id, data)
    if (mountedRef.current) {
      await fetchStudents()
    }
  }, [fetchStudents])

  const removeStudent = useCallback(async (id: string) => {
    await deleteStudent(id)
    if (mountedRef.current) {
      await fetchStudents()
    }
  }, [fetchStudents])

  const resetPassword = useCallback(async (id: string, newPassword?: string) => {
    const result = await resetStudentPassword(id, newPassword)
    return result.generatedPassword
  }, [])

  const addGroup = useCallback(async (name: string, tutorIds?: string[]) => {
    const newGroup = await createGroup(name, tutorIds)
    if (mountedRef.current) {
      await fetchGroups()
    }
    return newGroup
  }, [fetchGroups])

  const editGroup = useCallback(async (id: string, name: string) => {
    await updateGroup(id, name)
    if (mountedRef.current) {
      await fetchGroups()
    }
  }, [fetchGroups])

  const removeGroup = useCallback(async (id: string) => {
    await deleteGroup(id)
    if (mountedRef.current) {
      await fetchGroups()
    }
  }, [fetchGroups])

  const totalPages = pagination ? pagination.pages : 1

  return {
    // Data
    students,
    groups,
    grades,
    loading,
    error,
    pagination,
    totalPages,

    // Filters
    filters,
    setSearch,
    setGroupFilter,
    setPage,

    // Mutations
    editStudent,
    removeStudent,
    resetPassword,
    addGroup,
    editGroup,
    removeGroup,

    // Refresh
    fetchAll,
    fetchStudents,
    fetchGroups,
  }
}
